require("./threepl-train-voucher-containers-table-row.scss");

import {ChangeDetectionStrategy, Component, DestroyRef, OnChanges, SimpleChanges, computed, inject, input, output, signal} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {BzSelectOption} from "@bringoz/ui";
import {bzIconTrash} from "@bringoz/ui/icons";
import {TrainContainerDetails} from "../../../models/train-container-details.model";
import {ThreePLPackingGroupType} from "../../../types/threePL-packing-group.type";
import {HazardousMaterial, TrainCar, UnNumberOption} from "../../models/train-provider-settings.model";
import {SharedCarChange} from "../threepl-train-voucher-containers-table/threepl-train-voucher-containers-table.component";
import {TrainContainersValidity} from "../../state/train-containers-validity";

export type ContainerFlag = "isEmpty" | "closePacking" | "middleLocation" | "weighted" | "contentDeclared";

interface SelectedHazardousMaterial {
  id?: string;
  descriptionEn?: string;
}

@Component({
  selector: "app-threepl-train-voucher-containers-table-row",
  templateUrl: "./threepl-train-voucher-containers-table-row.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: "tr"},
})
export class ThreeplTrainVoucherContainersTableRowComponent implements OnChanges {
  readonly container = input.required<TrainContainerDetails>();
  readonly cars = input<TrainCar[]>([]);
  readonly isSharedCar = input<boolean | undefined>(undefined);
  readonly hazardousMaterials = input<HazardousMaterial[]>([]);
  readonly unNumbers = input<UnNumberOption[]>([]);
  readonly isEdit = input(false);
  readonly submitted = input(false);

  readonly containerChange = output<TrainContainerDetails>();
  readonly isSharedCarChange = output<SharedCarChange>();
  readonly remove = output<TrainContainerDetails>();

  readonly trashIcon = bzIconTrash;
  readonly containerTypes: BzSelectOption<string>[] = [{value: "20", label: "20"}, {value: "40", label: "40"}];

  readonly selectedCar = signal<TrainCar | undefined>(undefined);
  readonly selectedHazardousMaterial = signal<SelectedHazardousMaterial | undefined>(undefined);
  readonly unNumbersOptions = signal<UnNumberOption[]>([]);
  readonly sharedCar = computed(() => this.isSharedCar() ?? false);

  private readonly translate = inject(TranslateService);

  readonly carInvalid = computed(() => !this.cars().some((car) => car.carId === this.container().carId));
  readonly weightInvalid = computed(() => {
    const weight = this.container().item?.weight;
    return weight === undefined || weight === null || weight === "";
  });
  readonly containerTypeInvalid = computed(() => !this.container().containerType);
  readonly materialInvalid = computed(() => !!this.selectedHazardousMaterial() && !this.container().materialInfo);
  readonly valid = computed(() =>
    !this.carInvalid() && !this.weightInvalid() && !this.containerTypeInvalid() && !this.materialInvalid());

  constructor() {
    const validity = inject(TrainContainersValidity, {optional: true});
    if (validity) {
      validity.register(this, this.valid);
      inject(DestroyRef).onDestroy(() => validity.unregister(this));
    }
  }

  readonly carOptions = computed<BzSelectOption<string>[]>(() =>
    this.cars().map((car) => ({value: car.carId, label: `${car.id} ${car.capacity} ${car.carType}`})));

  readonly unOptions = computed<BzSelectOption<string | null>[]>(() => [
    {value: null, label: this.translate.instant("None")},
    ...this.hazardousMaterials().map((m) => ({value: m.id, label: `${m.id} ${m.descriptionEn}`})),
  ]);

  readonly materialOptions = computed<BzSelectOption<string | null>[]>(() => [
    {value: null, label: this.translate.instant("None")},
    ...this.unNumbersOptions().map((u) => ({value: u.materialCode, label: `${u.materialCode} ${u.description}`})),
  ]);

  readonly packingGroupOptions = computed<BzSelectOption<ThreePLPackingGroupType>[]>(() =>
    Object.values(ThreePLPackingGroupType).map((type) => ({value: type, label: this.translate.instant(`TRAIN_IL_MATERIAL_INFO.${type}`)})));

  ngOnChanges(changes: SimpleChanges): void {
    const later = (key: string) => changes[key] && !changes[key].isFirstChange();
    if (later("container") || later("cars") || later("hazardousMaterials")) {
      const container = this.container();
      this.selectedHazardousMaterial.set({id: container.unNumberInfo?.id, descriptionEn: container.unNumberInfo?.description});
      this.selectedCar.set(this.cars().find((car) => car.id === container.carId));
    }
  }

  onCarSelect(carId: string): void {
    const container = this.container();
    container.carId = carId;
    this.selectedCar.set(this.cars().find((car) => car.carId === carId));
    this.containerChange.emit(container);
  }

  onUnNumberSelect(materialId: string | null): void {
    const material = this.hazardousMaterials().find((m) => m.id === materialId);
    this.selectedHazardousMaterial.set(material);
    if (!material) {
      return;
    }
    const container = this.container();
    container.unNumberInfo = {id: material.id, description: material.descriptionEn};
    this.unNumbersOptions.set(this.unNumbers().filter((u) => u.unNumber === material.id));
    container.materialInfo = undefined;
    this.containerChange.emit(container);
  }

  onMaterialSelect(materialCode: string | null): void {
    const option: UnNumberOption & {id?: string} = this.unNumbersOptions().find((u) => u.materialCode === materialCode);
    if (option) {
      option.id = option.materialCode;
    }
    const container = this.container();
    container.materialInfo = option;
    this.containerChange.emit(container);
  }

  onContainerTypeChange(containerType: string): void {
    const container = this.container();
    container.containerType = containerType;
    this.containerChange.emit(container);
  }

  onPackingGroupChange(packingGroup: ThreePLPackingGroupType): void {
    this.container().packingGroup = packingGroup;
  }

  onFlagChange(flag: ContainerFlag, value: boolean): void {
    const container = this.container();
    container[flag] = value;
    this.containerChange.emit(container);
  }

  onSharedToggle(): void {
    this.isSharedCarChange.emit({carId: this.container().carId, isSharedCar: !this.sharedCar()});
  }
}
